import { Collection } from '@msw/data'
import { persist } from '@msw/data/extensions/persist'
import type { OrderDetailsExtended } from 'app/src/model'
import { getAllOrders } from 'app/src/msw/shopping/initialSeed/orders'
import { creditSchema } from 'app/src/msw/shopping/schemas/credit'

const creditCollection = new Collection({
  schema: creditSchema,
  extensions: [persist()],
})

export { creditCollection }

const creditDataFakeMembership1 = {
  creditByServiceCategory: {
    welfareArt100: {
      available: 1000,
      total: 1000,
    },
    welfareArt51: {
      available: 1000,
      total: 1000,
    },
  },
  fringeAssigned: 1000,
  info: ['note'],
  pdrAssigned: 1000,
  membershipId: 'fakeMembership1',
}

const creditDataFakeMembership2 = {
  creditByServiceCategory: {
    welfareArt100: {
      available: 1000,
      total: 1000,
    },
    welfareArt51: {
      available: 1000,
      total: 1000,
    },
  },
  fringeAssigned: 1000,
  info: ['note'],
  pdrAssigned: 1000,
  membershipId: 'fakeMembership2',
}

const creditDataFakeMembership3 = {
  creditByServiceCategory: {
    welfareArt100: {
      available: 1000,
      total: 1000,
    },
    welfareArt51: {
      available: 1000,
      total: 1000,
    },
  },
  fringeAssigned: 0,
  info: ['note'],
  pdrAssigned: 1000,
  membershipId: 'fakeMembership3',
}

export const createCreditSeed = async () => {
  const creditExistingData = await creditCollection.findMany()

  console.log('MSW: Checking Credit data, found', creditExistingData.length)

  if (creditExistingData.length > 0) {
    return
  }

  const fakeMembership1Used = await getUsedCredit('fakeMembership1')
  const fakeMembership2Used = await getUsedCredit('fakeMembership2')
  const fakeMembership3Used = await getUsedCredit('fakeMembership3')

  const createdCredit = await creditCollection.create({
    ...creditDataFakeMembership1,
    creditByServiceCategory: {
      welfareArt100: {
        available: creditDataFakeMembership1.creditByServiceCategory.welfareArt100.available - fakeMembership1Used.art100Used,
        total: creditDataFakeMembership1.creditByServiceCategory.welfareArt100.total,
      },
      welfareArt51: {
        available: creditDataFakeMembership1.creditByServiceCategory.welfareArt51.available - fakeMembership1Used.art51Used,
        total: creditDataFakeMembership1.creditByServiceCategory.welfareArt51.total,
      },
    },
  })
  const createdCredit2 = await creditCollection.create({
    ...creditDataFakeMembership2,
    creditByServiceCategory: {
      welfareArt100: {
        available: creditDataFakeMembership2.creditByServiceCategory.welfareArt100.available - fakeMembership2Used.art100Used,
        total: creditDataFakeMembership2.creditByServiceCategory.welfareArt100.total,
      },
      welfareArt51: {
        available: creditDataFakeMembership2.creditByServiceCategory.welfareArt51.available - fakeMembership2Used.art51Used,
        total: creditDataFakeMembership2.creditByServiceCategory.welfareArt51.total,
      },
    },
  })
  const createdCredit3 = await creditCollection.create({
    ...creditDataFakeMembership3,
    creditByServiceCategory: {
      welfareArt100: {
        available: creditDataFakeMembership3.creditByServiceCategory.welfareArt100.available - fakeMembership3Used.art100Used,
        total: creditDataFakeMembership3.creditByServiceCategory.welfareArt100.total,
      },
      welfareArt51: {
        available: creditDataFakeMembership3.creditByServiceCategory.welfareArt51.available - fakeMembership3Used.art51Used,
        total: creditDataFakeMembership3.creditByServiceCategory.welfareArt51.total,
      },
    },
  })
  console.log('createCreditSeed', createdCredit, createdCredit2, createdCredit3)
}

export const updateCreditSeed = async (art51Total: number, art100Total: number) => {
  const activeMembership = JSON.parse(sessionStorage.getItem('selectedCampaign') || '{}')

  const creditExistingData = await creditCollection.findFirst((q) => q.where({ membershipId: activeMembership?.membershipId }))

  const { art51Used, art100Used } = await getStaticUsedCreditByMembershipId(activeMembership?.membershipId)

  if (creditExistingData) {
    await creditCollection.update(creditExistingData, {
      data(credit) {
        credit.creditByServiceCategory.welfareArt51.total = art51Total
        credit.creditByServiceCategory.welfareArt51.available = art51Total - art51Used
        credit.creditByServiceCategory.welfareArt100.total = art100Total
        credit.creditByServiceCategory.welfareArt100.available = art100Total - art100Used
      },
    })
  }
}

export const restoreCreditSeed = async () => {
  await creditCollection.clear()
  await createCreditSeed()
}

export const getUsedCredit = async (membershipId: string) => {
  const orders = (await getAllOrders()).filter(
    (o) =>
      o.userInfo?.membershipId === membershipId && o.status !== 'canceled' && o.status !== 'rejected' && o.status !== 'failed',
  )

  return {
    art51Used: orders.reduce(
      (sum, order) => sum + (order.product.serviceCategory === 'welfareArt51' ? order.priceDetails?.grossPrice || 0 : 0),
      0,
    ),
    art100Used: orders.reduce((sum, order) => sum + (order.priceDetails?.grossPrice || 0), 0),
  }
}

export const getStaticUsedCredit = (orders: OrderDetailsExtended[], membershipId: string) => {
  const filteredOrders = orders?.filter(
    (o) =>
      o.userInfo?.membershipId === membershipId && o.status !== 'canceled' && o.status !== 'rejected' && o.status !== 'failed',
  )

  return {
    art51Used: filteredOrders?.reduce(
      (sum, order) => sum + (order.product.serviceCategory === 'welfareArt51' ? order.priceDetails?.grossPrice || 0 : 0),
      0,
    ),
    art100Used: filteredOrders?.reduce((sum, order) => sum + (order.priceDetails?.grossPrice || 0), 0),
  }
}

export const getAllStaticOrders = async (): Promise<OrderDetailsExtended[]> => {
  const discountOrders = await (await fetch('./data/shopping/discountOrders.json')).json()
  const giftCardsOrders = await (await fetch('./data/shopping/giftCardsOrders.json')).json()
  const goodAndServiceOrders = await (await fetch('./data/shopping/goodAndServiceOrders.json')).json()
  const refundOrders = await (await fetch('./data/shopping/refundsOrders.json')).json()
  const vouchersOrders = await (await fetch('./data/shopping/vouchersOrders.json')).json()
  const transferOrders = await (await fetch('./data/shopping/transfersOrders.json')).json()

  return [
    ...(discountOrders?.items || []),
    ...(giftCardsOrders?.items || []),
    ...(goodAndServiceOrders?.items || []),
    ...(refundOrders?.items || []),
    ...(vouchersOrders?.items || []),
    ...(transferOrders?.items || []),
  ]
}

export const getStaticUsedCreditByMembershipId = async (membershipId: string) => {
  const orders = await getAllStaticOrders()

  const filteredOrders = orders?.filter(
    (o) =>
      o.userInfo?.membershipId === membershipId && o.status !== 'canceled' && o.status !== 'rejected' && o.status !== 'failed',
  )

  return {
    art51Used: filteredOrders?.reduce(
      (sum, order) => sum + (order.product.serviceCategory === 'welfareArt51' ? order.priceDetails?.grossPrice || 0 : 0),
      0,
    ),
    art100Used: filteredOrders?.reduce((sum, order) => sum + (order.priceDetails?.grossPrice || 0), 0),
  }
}
